import React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BarChart3,
  BookOpenCheck,
  Building2,
  CalendarCheck,
  FileCheck2,
  HandHeart,
  Landmark,
  ShieldCheck,
  Users,
} from "lucide-react";

const treasuryData = [
  { month: "Jan", entradas: 38600, saidas: 24100 },
  { month: "Fev", entradas: 41200, saidas: 26800 },
  { month: "Mar", entradas: 39800, saidas: 25300 },
  { month: "Abr", entradas: 46700, saidas: 29200 },
  { month: "Mai", entradas: 49300, saidas: 31500 },
  { month: "Jun", entradas: 52800, saidas: 32100 },
];

const lodges = [
  { name: "Loja Fictícia Aurora nº 101", members: 148, frequency: "91%", balance: "R$ 84.320" },
  { name: "Loja Fictícia Harmonia nº 202", members: 136, frequency: "86%", balance: "R$ 67.540" },
  { name: "Loja Fictícia Horizonte nº 303", members: 144, frequency: "88%", balance: "R$ 71.890" },
];

const metrics = [
  { label: "Lojas fictícias", value: "03", detail: "ambientes demonstrativos", icon: Building2 },
  { label: "Membros ativos", value: "428", detail: "cadastros simulados", icon: Users },
  { label: "Sessões realizadas", value: "72", detail: "no ano demonstrativo", icon: CalendarCheck },
  { label: "Frequência média", value: "88%", detail: "presença fictícia", icon: BookOpenCheck },
];

const modules = [
  { title: "Secretaria", text: "Membros, candidatos, sessões, balaústres e documentos.", icon: FileCheck2 },
  { title: "Chancelaria", text: "Presença, frequência, datas comemorativas e check-in.", icon: CalendarCheck },
  { title: "Tesouraria", text: "Capitações, receitas, despesas, contas e centros de custo.", icon: Landmark },
  { title: "Filantropia", text: "Campanhas, destinação de recursos e transparência.", icon: HandHeart },
  { title: "Relatórios", text: "Balanços anônimos, frequência, saldos e exportações.", icon: BarChart3 },
  { title: "Governança", text: "Perfis, permissões, auditoria e isolamento por Loja.", icon: ShieldCheck },
];

const money = (value) => `R$ ${(value / 1000).toFixed(0)} mil`;

export const PublicDemoDashboard = () => (
  <div className="space-y-5" data-testid="public-demo-dashboard">
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-4">
      <div>
        <p className="font-brand text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Visão geral da plataforma</p>
        <h2 className="mt-1 font-serif text-2xl font-bold text-white">Resumo operacional fictício</h2>
      </div>
      <div data-testid="demo-badge" className="inline-flex items-center gap-2 border border-amber-400/30 bg-amber-400/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-300">
        <span className="h-2 w-2 animate-pulse rounded-full bg-amber-300" /> Ambiente de demonstração
      </div>
    </div>

    <div className="grid grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/10 lg:grid-cols-4">
      {metrics.map(({ label, value, detail, icon: Icon }, index) => (
        <div key={label} data-testid={`demo-metric-${index}`} className="min-w-0 bg-[#111827] p-4">
          <div className="flex items-start justify-between gap-3">
            <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">{label}</p>
            <Icon className="h-4 w-4 shrink-0 text-[#D4AF37]" strokeWidth={1.5} />
          </div>
          <p className="mt-3 font-serif text-2xl font-bold text-white">{value}</p>
          <p className="mt-1 text-[10px] text-slate-500">{detail}</p>
        </div>
      ))}
    </div>

    <div className="grid gap-5 xl:grid-cols-12">
      <div className="border border-white/10 bg-[#111827] p-5 xl:col-span-7" data-testid="demo-treasury-chart">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-emerald-400">Tesouraria consolidada</p>
            <p className="mt-1 font-serif text-lg font-bold text-white">Entradas e saídas fictícias</p>
          </div>
          <p className="font-mono text-sm font-bold text-[#D4AF37]">Saldo R$ 73.750</p>
        </div>
        <div className="h-44 min-h-44 w-full">
          <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 620, height: 176 }}>
            <AreaChart data={treasuryData} margin={{ top: 5, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="demoIncome" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#263247" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748B" fontSize={9} tickFormatter={money} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #334155", fontSize: 11 }} formatter={(value) => [`R$ ${Number(value).toLocaleString("pt-BR")}`, ""]} />
              <Area type="monotone" dataKey="entradas" stroke="#10B981" fill="url(#demoIncome)" strokeWidth={2} />
              <Area type="monotone" dataKey="saidas" stroke="#F43F5E" fill="transparent" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-white/10 bg-[#111827] p-5 xl:col-span-5" data-testid="demo-operations-summary">
        <p className="text-[9px] font-bold uppercase tracking-widest text-sky-300">Atividade simulada</p>
        <div className="mt-4 space-y-4">
          {[
            ["Sessão Magna fictícia", "18 Jun", "42 presentes"],
            ["Campanha Cesta Fraterna", "Em andamento", "76% da meta"],
            ["Balanço consolidado", "Atualizado", "Sem dados pessoais"],
            ["Documentos aprovados", "Este mês", "12 balaústres"],
          ].map(([title, status, value], index) => (
            <div key={title} data-testid={`demo-activity-${index}`} className="grid grid-cols-[1fr_auto] gap-3 border-b border-white/10 pb-3 last:border-0">
              <div className="min-w-0"><p className="truncate text-xs font-semibold text-slate-100">{title}</p><p className="mt-1 text-[10px] text-slate-500">{status}</p></div>
              <p className="text-right text-[10px] font-semibold text-[#D4AF37]">{value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const PublicDemoDetails = () => (
  <section className="relative z-10 border-y border-white/10 bg-[#0D1320] py-20" data-testid="public-demo-details">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl">
        <p className="font-brand text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Dados fixos e demonstrativos</p>
        <h2 className="mt-3 font-serif text-3xl font-bold text-white sm:text-4xl">Todas as áreas em um único resumo</h2>
        <p className="mt-4 text-sm leading-relaxed text-slate-400">Os números abaixo servem apenas para apresentar os recursos. Nenhuma Loja real ou informação de usuário é exibida.</p>
      </div>

      <div className="mt-10 grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {modules.map(({ title, text, icon: Icon }, index) => (
          <article key={title} data-testid={`demo-module-${index}`} className="bg-[#111827] p-6 transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-[#151F32]">
            <Icon className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
            <h3 className="mt-5 font-serif text-lg font-bold text-white">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{text}</p>
          </article>
        ))}
      </div>

      <div className="mt-12 overflow-x-auto border border-white/10 bg-[#111827]" data-testid="demo-lodges-table">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-5 py-4">
          <h3 className="font-serif text-lg font-bold text-white">Comparativo de Lojas fictícias</h3>
          <span className="text-[10px] font-bold uppercase tracking-widest text-amber-300">Não representa Lojas reais</span>
        </div>
        <table className="min-w-[680px] w-full text-left text-sm">
          <thead><tr className="border-b border-white/10 text-[10px] uppercase tracking-widest text-slate-500"><th className="px-5 py-3">Loja demonstrativa</th><th className="px-5 py-3 text-right">Membros</th><th className="px-5 py-3 text-right">Frequência</th><th className="px-5 py-3 text-right">Saldo fictício</th></tr></thead>
          <tbody>{lodges.map((lodge, index) => <tr key={lodge.name} data-testid={`demo-lodge-row-${index}`} className="border-b border-white/5 text-slate-300 last:border-0 hover:bg-white/[0.03]"><td className="px-5 py-4 font-semibold text-white">{lodge.name}</td><td className="px-5 py-4 text-right">{lodge.members}</td><td className="px-5 py-4 text-right text-emerald-400">{lodge.frequency}</td><td className="px-5 py-4 text-right font-mono text-[#D4AF37]">{lodge.balance}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  </section>
);
